import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/shared/lib/supabase";
import { Button } from "@/shared/components/ui/button";
import { Input } from "@/shared/components/ui/input";
import { FieldError } from "@/shared/components/ui/FieldError";
import { SecurityNote } from "@/shared/components/ui/security-note";
import { AUTH_ROUTES, AUTH_QUERY_PARAMS, AUTH_SOURCES } from "@/shared/constants/paths";
import { useSignInRoute } from "@/shared/hooks/useSignInRoute";
import { sentryUtils } from "@/shared/utils/sentry";
import { ChevronLeft, ArrowRight, Loader2 } from "lucide-react";
import FullscreenLayout from "@/pages/layouts/FullScreenLayout";
import forgotPasswordImg from "@/shared/assets/illustrations/forgot_password.svg";

const ForgotPasswordPage = () => {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [validationError, setValidationError] = useState("");
  const [serverError, setServerError] = useState("");
  const [success, setSuccess] = useState("");
  const navigate = useNavigate();
  const { isFromLawyer, signInRoute } = useSignInRoute();

  const handleSubmit = async () => {
    if (!email) {
      setValidationError("Enter your email address");
      return;
    }

    if (!email.includes("@")) {
      setValidationError("Enter a valid email address");
      return;
    }

    setLoading(true);
    setValidationError("");
    setServerError("");
    setSuccess("");

    try {
      const genericMessage =
        "If an account with that email exists, we've sent you a password reset link.";

      const resetPasswordUrl = new URL(AUTH_ROUTES.RESET_PASSWORD, window.location.origin);
      if (isFromLawyer) {
        resetPasswordUrl.searchParams.set(AUTH_QUERY_PARAMS.FROM, AUTH_SOURCES.LAWYER);
      }

      const { error: resetError } = await supabase.auth.resetPasswordForEmail(
        email.trim().toLowerCase(),
        {
          redirectTo: resetPasswordUrl.toString(),
        },
      );

      if (resetError) {
        console.error("Reset password error:", resetError);
        setServerError("The reset link wasn't sent. Try again.");
        sentryUtils.logAuthenticationEvent("password_reset_requested");
      } else {
        setSuccess(genericMessage);
      }
    } catch (err) {
      console.error("Password reset error:", err);
      setServerError("We couldn't send the link. Try again in a moment.");
      sentryUtils.captureError(err, "Password reset request failed");
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    navigate(signInRoute);
  };

  const backButton = (
    <Button variant="outline" onClick={handleBack}>
      <ChevronLeft className="h-4 w-4" />
      <span className="hidden sm:inline">Back</span>
    </Button>
  );

  if (success) {
    return (
      <FullscreenLayout showTopBar topBarLeftContent={backButton}>
        <div className="flex flex-1 items-center justify-center px-6">
          <div className="w-full max-w-lg text-center">
            <h1 className="mb-2">Check your email</h1>
            <p className="text-secondary mb-8">{success}</p>
            <Button onClick={handleBack} variant="brand" rounded className="w-full">
              Back to log in
            </Button>
          </div>
        </div>
      </FullscreenLayout>
    );
  }

  return (
    <FullscreenLayout showTopBar topBarLeftContent={backButton}>
      <div className="flex flex-1 items-start justify-center px-6 pt-8 sm:items-center sm:pt-0">
        <div className="w-full max-w-lg">
          <div className="flex justify-center mb-6">
            <img src={forgotPasswordImg} alt="Reset password" className="h-[150px]" />
          </div>

          <h1 className="mb-2 text-center">Reset your password</h1>

          <p className="text-secondary mb-8 text-center">
            Enter your email address and we'll send you a link to reset your password.
          </p>

          <div className="flex flex-col gap-1.5 mb-8">
            <label htmlFor="email" className="text-sm font-medium text-primary">
              Email
            </label>
            <Input
              id="email"
              type="email"
              value={email}
              onChange={(e) => {
                setEmail(e.target.value);
                setValidationError("");
              }}
              placeholder="Enter your email"
              onKeyDown={(e) => e.key === "Enter" && handleSubmit()}
              className={validationError ? "border-destructive border-[2px]" : ""}
            />
            <FieldError message={validationError || serverError} />
          </div>

          <Button
            onClick={handleSubmit}
            disabled={loading}
            variant="brand"
            rounded
            className="w-full"
          >
            {loading ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <>
                Send reset link <ArrowRight className="h-4 w-4" />
              </>
            )}
          </Button>

          <div className="mt-6">
            <SecurityNote text="Your information is encrypted and never sold." />
          </div>
        </div>
      </div>
    </FullscreenLayout>
  );
};

export default ForgotPasswordPage;
